<mat-progress-bar *ngIf="loading && !picture" mode="indeterminate"></mat-progress-bar>
<p *ngIf="refreshing" class="refresh-hint">Refreshing role data...</p>

<div class="toolbar">
  <button mat-stroked-button type="button" (click)="load(true)" [disabled]="loading">
    <mat-icon>refresh</mat-icon>
    Reload
  </button>
</div>

<mat-card class="filters-card section-card">
  <div class="filters-header">
    <div>
      <h2>Dependency graph</h2>
      <p class="hint">
        Tree-style view of skill and topic dependencies for the selected role profile. Roots are shown first, and names are resolved from the role graph. Use Edit on a node to change its dependencies.
      </p>
    </div>
    <div class="summary" *ngIf="picture">
      {{ filteredSections().length }}/{{ sections().length }} profiles
    </div>
  </div>

  <div class="filters-row">
    <mat-form-field appearance="outline">
      <mat-label>Specialization</mat-label>
      <mat-select [ngModel]="selectedSpecialization()" (ngModelChange)="selectedSpecialization.set($event)">
        <mat-option *ngFor="let option of specializationOptions()" [value]="option.value">
          {{ option.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Seniority</mat-label>
      <mat-select [ngModel]="selectedSeniority()" (ngModelChange)="selectedSeniority.set($event)">
        <mat-option *ngFor="let option of seniorityOptions()" [value]="option.value">
          {{ option.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>
  </div>
</mat-card>

<mat-card *ngIf="error" class="error-card section-card">
  <h2>Could not load dependency graph</h2>
  <p>{{ error }}</p>
</mat-card>

<mat-card *ngIf="!loading && picture && !filteredSections().length" class="empty-card section-card">
  <h2>No matching profiles</h2>
  <p>Try a different specialization or seniority filter.</p>
</mat-card>

<ng-template #graphNode let-node let-section="section">
  <li class="graph-node-item">
    <div class="graph-node-card" [class.topic-node]="node.kind === 'topic'">
      <div class="graph-node-header">
        <div class="graph-node-copy">
          <div class="graph-node-title">{{ node.label }}</div>
          <div class="graph-node-subtitle" *ngIf="node.subtitle">{{ node.subtitle }}</div>
        </div>
        <div class="graph-node-actions">
          <mat-chip-set>
            <mat-chip>{{ node.kind }}</mat-chip>
            <mat-chip *ngIf="node.children.length">{{ node.children.length }} downstream</mat-chip>
            <mat-chip *ngIf="node.unresolvedDependencies.length" class="warning-chip">missing refs</mat-chip>
          </mat-chip-set>
          <button
            mat-icon-button
            type="button"
            (click)="openEditNode(node, section)"
            [disabled]="loading || refreshing"
            [title]="node.kind === 'skill' ? 'Edit skill dependencies' : 'Edit topic dependencies'">
            <mat-icon>tune</mat-icon>
          </button>
        </div>
      </div>

      <p *ngIf="node.dependencyLabels.length" class="node-meta">
        Depends on {{ node.dependencyLabels.join(', ') }}
      </p>
      <p *ngIf="node.unresolvedDependencies.length" class="node-warning">
        Unresolved: {{ node.unresolvedDependencies.join(', ') }}
      </p>
    </div>

    <ul *ngIf="node.children.length" class="graph-children">
      <ng-container *ngFor="let child of node.children; trackBy: trackByNode">
        <ng-container *ngTemplateOutlet="graphNode; context: { $implicit: child, section: section }"></ng-container>
      </ng-container>
    </ul>
  </li>
</ng-template>

<section *ngFor="let section of filteredSections(); trackBy: trackBySection" class="section-stack">
  <mat-card class="section-card">
    <div class="section-header">
      <div>
        <h2>{{ section.heading }}</h2>
        <p class="hint">
          {{ section.skillNodeCount }} skills, {{ section.topicNodeCount }} topics, {{ totalRoots(section) }} roots
        </p>
      </div>
      <mat-chip-set>
        <mat-chip>{{ section.specializationName }}</mat-chip>
        <mat-chip>{{ formatLevel(section.seniorityLevel) }}</mat-chip>
      </mat-chip-set>
    </div>

    <div *ngIf="!hasGraph(section)" class="empty-state">
      No dependencies or topics are available for this profile yet.
    </div>

    <div *ngIf="hasGraph(section)" class="graph-columns">
      <section class="graph-panel">
        <div class="panel-header">
          <h3>Skill dependencies</h3>
          <span class="panel-count">{{ section.skillRoots.length }} roots</span>
        </div>
        <p *ngIf="!section.skillRoots.length" class="empty-state">No skill dependency roots in this profile.</p>
        <ul *ngIf="section.skillRoots.length" class="graph-tree">
          <ng-container *ngFor="let root of section.skillRoots; trackBy: trackByNode">
            <ng-container *ngTemplateOutlet="graphNode; context: { $implicit: root, section: section }"></ng-container>
          </ng-container>
        </ul>
      </section>

      <section class="graph-panel">
        <div class="panel-header">
          <h3>Topic dependencies</h3>
          <span class="panel-count">{{ section.topicRoots.length }} roots</span>
        </div>
        <p *ngIf="!section.topicRoots.length" class="empty-state">No topic dependency roots in this profile.</p>
        <ul *ngIf="section.topicRoots.length" class="graph-tree">
          <ng-container *ngFor="let root of section.topicRoots; trackBy: trackByNode">
            <ng-container *ngTemplateOutlet="graphNode; context: { $implicit: root, section: section }"></ng-container>
          </ng-container>
        </ul>
      </section>
    </div>
  </mat-card>
</section>